import React, { useState, useCallback, useRef, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Avatar } from "../design/avatar";
import { Button } from "../design/button";
import { CameraIcon, XMarkIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

interface ProfileImageUploadProps {
  currentImageUrl?: string | null;
  initials?: string;
  onFileSelected: (file: File | null) => void;
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
}

const sizeClasses = {
  sm: "w-16 h-16",
  md: "w-24 h-24",
  lg: "w-32 h-32",
};

const ProfileImageUpload: React.FC<ProfileImageUploadProps> = ({
  currentImageUrl,
  initials,
  onFileSelected,
  size = "md",
  disabled = false,
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      // Revoke previous preview URL if exists
      if (preview) {
        URL.revokeObjectURL(preview);
      }
      const newPreview = URL.createObjectURL(file);
      setPreview(newPreview);
      onFileSelected(file);
    }
  }, [preview, onFileSelected]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"],
    },
    maxFiles: 1,
    disabled,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
  });

  const handleRemove = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    onFileSelected(null);
  }, [preview, onFileSelected]);

  const displayImage = preview || currentImageUrl;
  const hasNewImage = !!preview;

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Image Container with Dropzone */}
      <div
        {...getRootProps()}
        className={clsx(
          "relative group cursor-pointer rounded-full transition-all duration-200",
          sizeClasses[size],
          isDragActive || isDragging
            ? "ring-4 ring-blue-400 ring-offset-2"
            : "hover:ring-4 hover:ring-zinc-300 dark:hover:ring-zinc-600 hover:ring-offset-2",
          disabled && "opacity-60 cursor-not-allowed"
        )}
      >
        <input {...getInputProps()} ref={fileInputRef} />

        {/* Avatar Display */}
        <Avatar
          src={displayImage}
          initials={!displayImage ? initials : undefined}
          alt="Profile image"
          className={clsx("w-full h-full", sizeClasses[size])}
        />

        {/* Hover Overlay */}
        {!disabled && (
          <div className={clsx(
            "absolute inset-0 flex items-center justify-center rounded-full transition-opacity duration-200",
            "bg-black/50",
            isDragActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          )}>
            <CameraIcon className="w-8 h-8 text-white" />
          </div>
        )}

        {/* Badge for new image */}
        {hasNewImage && !disabled && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-white dark:border-zinc-900">
            <ArrowPathIcon className="w-3 h-3 text-white" />
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        {hasNewImage && !disabled && (
          <Button
            type="button"
            plain
            onClick={handleRemove}
            className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
          >
            <XMarkIcon className="w-4 h-4 mr-1" />
            Remove
          </Button>
        )}
      </div>

      {/* Helper Text */}
      <p className="text-xs text-zinc-500 dark:text-zinc-400 text-center">
        {isDragActive
          ? "Drop image here..."
          : "Click or drag to upload"}
      </p>
    </div>
  );
};

export default ProfileImageUpload;
